import {
  getCarriersIndexSetting,
  toggleCarrierActive,
  previewCarrierByOrderData,
  previewCarrierByOrderId,
  previewCarrierByOrderExcelRow,
  getAvailableCarriers,
  fetchCarrierRestrictedItems,
} from '@/api';
import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import {
  CarrierIndexSettingResponse,
  CarrierPreviewResponse,
  CarrierPreviewRequest,
  AvailableCarriersRequest,
  AvailableCarriersAdaptedResponse,
  CarrierRestrictedItemsResponse,
  ErrorMessageResponse,
} from '@/types';
import { useQueryClient } from '@tanstack/react-query';

// Get Carriers Index Setting
export const useGetCarriersIndexSetting = (enabled = true) => {
  return useSentryQuery<CarrierIndexSettingResponse, Error>(
    ['carriers-index-setting'],
    () => getCarriersIndexSetting(),
    { enabled },
  );
};

// Toggle Carrier Active
export const useToggleCarrierActive = () => {
  const queryClient = useQueryClient();
  return useSentryMutation<CarrierIndexSettingResponse, ErrorMessageResponse, string>(
    (uid: string) => toggleCarrierActive(uid),
    {
      onMutate: async (uid: string) => {
        await queryClient.cancelQueries(['carriers-index-setting']);
        const previous = queryClient.getQueryData<CarrierIndexSettingResponse>(['carriers-index-setting']);
        if (previous?.object) {
          queryClient.setQueryData<CarrierIndexSettingResponse>(['carriers-index-setting'], {
            ...previous,
            object: previous.object.map((carrier) =>
              carrier.uid === uid
                ? { ...carrier, is_active_for_merchant: !carrier.is_active_for_merchant }
                : carrier,
            ),
          });
        }
        return { previous };
      },
      onError: (_err, _uid, context: any) => {
        if (context?.previous) {
          queryClient.setQueryData(['carriers-index-setting'], context.previous);
        }
      },
      onSettled: () => {
        // Silently refetch in background — optimistic update already shows correct state
        queryClient.invalidateQueries(['carriers-index-setting']);
      },
    },
  );
};

// Get Available Carriers — new Phase 2 endpoint, returns adapted legacy shape.
// defaultErrorMessage: '' suppresses automatic toasts — all error handling is done
// via the onError callback at the call site (NoCarrierModal, etc.).
export const useGetAvailableCarriers = () => {
  return useSentryMutation<
    AvailableCarriersAdaptedResponse,
    ErrorMessageResponse,
    AvailableCarriersRequest
  >((request: AvailableCarriersRequest) => getAvailableCarriers(request), {
    defaultErrorMessage: '',
  });
};

// Carrier Preview By Order Data (legacy — kept for backward compat)
export const useCarrierPreviewByOrderData = () => {
  return useSentryMutation<CarrierPreviewResponse, ErrorMessageResponse, CarrierPreviewRequest>(
    (request: CarrierPreviewRequest) => previewCarrierByOrderData(request),
  );
};

// Carrier Preview By Order ID
export const useCarrierPreviewByOrderId = () => {
  return useSentryMutation<CarrierPreviewResponse, ErrorMessageResponse, string>(
    (orderUid: string) => previewCarrierByOrderId(orderUid),
  );
};

// Fetch restricted items for a carrier — only fires when enabled=true (i.e. modal is open)
export const useFetchCarrierRestrictedItems = (slug: string, enabled: boolean = false) => {
  return useSentryQuery<CarrierRestrictedItemsResponse, Error>(
    ['carrier-restricted-items', slug],
    () => fetchCarrierRestrictedItems(slug),
    { enabled: enabled && !!slug },
  );
};

// Carrier Preview By Order Excel Row
export const useCarrierPreviewByOrderExcelRow = () => {
  return useSentryMutation<
    CarrierPreviewResponse,
    ErrorMessageResponse,
    { rowKey: string; pickupAddressRegionUid: string }
  >(({ rowKey, pickupAddressRegionUid }) =>
    previewCarrierByOrderExcelRow(rowKey, pickupAddressRegionUid),
  );
};
